import { useAppSelector, useAppTranslation } from 'app/hooks';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { isValidAmount } from './WithdrawModal';
import { Select } from 'antd';
import { selectCurrentWallet } from 'features/home/homeSlice';
import { formatAmountV2 } from 'utils/formatAmount';
import transferApi from 'api/transferApi';
import { showConfirmModal, showSuccessModal } from 'components/Modals/CommonModals';
import WarningIcon from 'assets/icons/WarningIcon';
import { useHandleResponseError } from 'hooks/useHandleResponseError';

interface P2pTabProps {
  isOpen: boolean;
}

const searchMethods = [
  {
    value: true,
    label: 'Email',
  },
  {
    value: false,
    label: 'Referral code',
  },
];

const P2pTab: React.FunctionComponent<P2pTabProps> = ({ isOpen }) => {
  const t = useAppTranslation();
  const currentWallet = useAppSelector(selectCurrentWallet);
  const handleResponseError = useHandleResponseError();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [formValue, setFormValue] = useState({
    searchInput: '',
    searchEmail: true,
    amount: '',
  });

  const handleChangeAmount = (event: React.ChangeEvent<HTMLInputElement>) => {
    const regex = /^$|^(1000000(\.0{0,2})?|[0-9]{1,6}(\.[0-9]{0,2})?|0\.)$/;
    const value = event.target.value;

    if (
      (regex.test(value) &&
        isValidAmount(value) &&
        currentWallet &&
        parseFloat(value) <= parseFloat(currentWallet.balance)) ||
      value === ''
    ) {
      setFormValue((prev) => ({ ...prev, amount: value }));
    }
  };

  const handleChangeSearchInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormValue((prev) => ({ ...prev, searchInput: event.target.value }));
  };

  const onSearchChange = (value: boolean) => {
    setFormValue((prev) => ({
      ...prev,
      searchEmail: value,
      searchInput: '',
    }));
  };

  const onTransfer = async (amount: number, userId: number) => {
    const { ok, error } = await transferApi.createOrder({
      userId,
      amount,
    });
    if (ok) {
      showSuccessModal({
        content: t('transfer.success.title'),
        onOk: () => {},
      });
    }
    if (error) {
      handleResponseError(error);
    }
  };

  const onPreview = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    const { ok, body, error } = await transferApi.searchUser({
      search: formValue.searchInput,
      searchEmail: formValue.searchEmail,
    });
    if (ok && body) {
      showConfirmModal({
        title: t('transfer.previewModal.title'),
        content: (
          <div className="preview-modal">
            <div className="preview-item">
              <p className="title">{t('transfer.previewModal.recipientEmail')}:</p>
              <p className="info">{body.email}</p>
            </div>
            <div className="preview-item">
              <p className="title">{t('transfer.previewModal.recipientName')}:</p>
              <p className="info">{body.userName}</p>
            </div>
            <div className="preview-item">
              <p className="title">{t('transfer.previewModal.sendAmount')}:</p>
              <p className="info">{formValue.amount} $</p>
            </div>
          </div>
        ),
        width: 600,
        icon: <WarningIcon />,
        onOk: () => onTransfer(parseFloat(formValue.amount), body.id),
        okText: t('button.confirm'),
        cancelText: t('button.cancel'),
      });
    }
    if (error) {
      handleResponseError(error);
    }
    setIsLoading(false);
    setFormValue((prev) => ({ ...prev, amount: '' }));
  };

  useEffect(() => {
    return () =>
      setFormValue({
        searchInput: '',
        searchEmail: true,
        amount: '',
      });
  }, [isOpen]);

  const disableButton = useMemo(() => {
    if (isLoading) {
      return true;
    }
    if (
      formValue.searchInput === '' ||
      formValue.amount === '' ||
      parseFloat(formValue.amount) === 0
    ) {
      return true;
    }
    return false;
  }, [isLoading, formValue.searchInput, formValue.amount]);

  return (
    <div>
      <form onSubmit={onPreview} className="transfer-form">
        <p className="form-title">{t('transfer.p2pTitle')}</p>
        <div className="input-container">
          <label className="primary-text-color">{t('transfer.search')}:</label>
          <Select
            className="antd__select"
            dropdownClassName="antd__select-dropdown"
            value={formValue.searchEmail}
            onChange={onSearchChange}
            options={searchMethods}
          />
        </div>
        <div className="input-container">
          <label className="primary-text-color">
            {formValue.searchEmail ? t('transfer.email') : t('transfer.referralCode')}:
          </label>
          <input
            type="text"
            name="withdraw-address"
            id="withdraw-address"
            autoComplete="off"
            placeholder={formValue.searchEmail ? t('transfer.email') : t('transfer.referralCode')}
            onChange={handleChangeSearchInput}
            value={formValue.searchInput}
          />
        </div>
        <div className="input-container">
          <label className="primary-text-color">{t('cashier.amount')}:</label>
          <input
            type="text"
            name="withdraw-address"
            id="withdraw-address"
            autoComplete="off"
            onChange={handleChangeAmount}
            value={formValue.amount}
            placeholder={'Send Amount'}
          />
          <div className="input-info">
            <p className="title">{t('balance')}:</p>
            <p className="info">{formatAmountV2(currentWallet?.balance, 2)} $TBET</p>
          </div>
        </div>
        <div className="button-container">
          <button disabled={disableButton} type="submit">
            {t('transfer.preview')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default P2pTab;
